import React from 'react';
import {
  Button, Stack, FormControl, FormCheck,
} from 'react-bootstrap';
import fileDownload from 'js-file-download';
import { FaCog } from 'react-icons/fa';
import api from 'api';

type FilterChange = {
  search: string | null;
  showInactive: boolean;
};

interface UserListFilterProps {
  initialSearch?: string | null;
  initialShowInactive?: boolean;
  debounceMs?: number;
  burnCenterId?: number | null;
  onChange: (next: FilterChange) => void;
}

const toNull = (s: string) => (s.trim() === '' ? null : s.trim());

const UserListFilter: React.FC<UserListFilterProps> = ({
  initialSearch = '',
  initialShowInactive = false,
  debounceMs = 400,
  onChange,
  burnCenterId = null,
}) => {
  const [searchValue, setSearchValue] = React.useState(initialSearch ?? '');
  const [showInactive, setShowInactive] = React.useState(!!initialShowInactive);
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    setSearchValue(initialSearch ?? '');
  }, [initialSearch]);
  React.useEffect(() => {
    setShowInactive(!!initialShowInactive);
  }, [initialShowInactive]);

  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const lastEmittedRef = React.useRef<string>(
    JSON.stringify({
      search:
        (initialSearch ?? '').trim() === ''
          ? null
          : (initialSearch ?? '').trim(),
      showInactive: !!initialShowInactive,
    }),
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const payload = {
        search: searchValue.trim() === '' ? null : searchValue.trim(),
        showInactive,
      };
      const key = JSON.stringify(payload);
      if (lastEmittedRef.current !== key) {
        lastEmittedRef.current = key;
        onChangeRef.current(payload);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, showInactive, debounceMs]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const payload = { search: toNull(searchValue), showInactive };
      const key = JSON.stringify(payload);
      if (lastEmittedRef.current !== key) {
        lastEmittedRef.current = key;
        onChangeRef.current(payload);
      }
    }
  };

  const onExport = async () => {
    try {
      setDownloading(true);
      const payload : any = {
        search: searchValue || null,
        includeInactive: !showInactive,
      };
      if (burnCenterId) {
        payload.burnCenterId = burnCenterId;
      }
      const { data } = await api.downloadUsers(payload);
      fileDownload(data, 'Users.csv');
    } catch {
      // alert('Error exporting file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack
      direction="horizontal"
      gap={2}
      className="flex-wrap justify-content-end align-items-center"
      style={{ minWidth: 0 }}
    >
      <FormControl
        placeholder="Search"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{ flex: '1 1 240px', minWidth: '200px', maxWidth: '320px' }}
      />
      <FormCheck id="showInactive" className="w-auto mb-0 flex-shrink-0">
        <FormCheck.Input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        <FormCheck.Label className="d-block text-nowrap">
          Include Inactive Users
        </FormCheck.Label>
      </FormCheck>
      <Button variant="secondary" onClick={onExport} disabled={downloading} className="flex-shrink-0">
        {downloading ? <FaCog className="fa-spin" /> : 'Export'}
      </Button>
    </Stack>
  );
};

export default UserListFilter;
